import { useState } from "react";
import moment from "moment";
import { Box, Grid, MenuItem, TextField, Typography } from "@mui/material";
import PropTypes from "prop-types";

import { ButtonV1, DatePickerV1 } from "components";
import useToaster from "hooks/useToaster";
import { OFF_DAY_WORKING, OFF_DAY_WORKING_OPTIONS } from "constants/eddConfig";
import { validateOffDay } from "utils/eddUtils";

const INITIAL_FORM = {
  date: null,
  working: "",
  start_time: "",
  end_time: "",
  reason: "",
};

const AddOffDayForm = ({ onAdd, isSubmitting = false, maxDate }) => {
  const triggerToaster = useToaster();
  const [form, setForm] = useState(INITIAL_FORM);

  const isTimeEnabled =
    Boolean(form.date) && form.working === OFF_DAY_WORKING.YES;

  const handleChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "working" && value !== OFF_DAY_WORKING.YES) {
        next.start_time = "";
        next.end_time = "";
      }
      return next;
    });
  };

  const handleAdd = async () => {
    const offDay = {
      date: form.date ? moment(form.date).format("YYYY-MM-DD") : null,
      is_working: form.working ? form.working === OFF_DAY_WORKING.YES : null,
      start_time: isTimeEnabled ? form.start_time || null : null,
      end_time: isTimeEnabled ? form.end_time || null : null,
      reason: form.reason.trim(),
      is_active: true,
    };
    const error = validateOffDay(offDay);
    if (error) {
      triggerToaster(error, "error");
      return;
    }
    const isSuccess = await onAdd(offDay);
    if (isSuccess) setForm(INITIAL_FORM);
  };

  return (
    <Box>
      <Grid container spacing={4}>
        <Grid item xs={12} sm={6}>
          <DatePickerV1
            label="Date"
            value={form.date}
            onChange={(val) => handleChange("date", val)}
            disabled={isSubmitting}
            disablePast
            maxDate={maxDate}
            dateStyle={{ width: "100%" }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            size="small"
            label="Working?"
            value={form.working}
            disabled={isSubmitting}
            onChange={(e) => handleChange("working", e.target.value)}
          >
            {OFF_DAY_WORKING_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="time"
            size="small"
            label="Start"
            value={form.start_time}
            disabled={!isTimeEnabled || isSubmitting}
            onChange={(e) => handleChange("start_time", e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 300 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="time"
            size="small"
            label="End"
            value={form.end_time}
            disabled={!isTimeEnabled || isSubmitting}
            onChange={(e) => handleChange("end_time", e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 300 }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label="Reason"
            placeholder="e.g. Diwali, plant maintenance"
            value={form.reason}
            disabled={isSubmitting}
            onChange={(e) => handleChange("reason", e.target.value)}
            inputProps={{ maxLength: 200 }}
          />
        </Grid>
      </Grid>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={4}
        mt={4}
      >
        <Typography fontSize={12} color="text.secondary">
          Start / End apply only to partial closure.
        </Typography>
        <ButtonV1
          title={isSubmitting ? "Adding..." : "Add"}
          size="small"
          onClick={handleAdd}
          loader={isSubmitting}
          disabled={!form.date || !form.working || isSubmitting}
        />
      </Box>
    </Box>
  );
};

AddOffDayForm.propTypes = {
  onAdd: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  maxDate: PropTypes.string,
};

export default AddOffDayForm;
